import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DEVICE_TYPES, ISeries, IYAxis } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { HIGHCHART_OPTIONS_DEFAULT, chartExportingOptions, getSeriesTableData } from 'app/data-studio-redesign/services/data-analysis-helper';
import * as Highcharts from 'highcharts';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';
import { DataAnalysisService } from 'app/data-studio-redesign/services/data-analysis-service';
import { CustomNumberFormatPipe, ThemeService } from '@wind-log_library/fe-component-library';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SignalsChartYAxisPopupComponent } from '../../time-series/signals-chart/signals-chart-y-axis-popup/signals-chart-y-axis-popup.component';
import moment from 'moment';
import { DataExportType, ExcelExportService } from 'app/shared/services/export/excel/excel-export.service';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';

@Component({
    selector: 'app-scatter-plot-signals-chart',
    templateUrl: './scatter-plot-signals-chart.component.html',
    styleUrls: ['./scatter-plot-signals-chart.component.scss'],
    standalone: false
})

export class ScatterPlotSignalsChartComponent implements OnInit, OnChanges, OnDestroy {
  
  @Input() signalsData: { data: ISeries[], yAxis: IYAxis[] };
  @Input() removeItems: { ids: string[], yAxis: IYAxis[] };
  @Input() updateSeriesList: ISeries[];
  @Input() xAxisMin: number;
  @Input() xAxisMax: number;
  @Input() updateFlag: boolean;
  @Input() exportChart$: ReplaySubject<string>;
  @Input() isReport: boolean;
  @Output() updated = new EventEmitter();
  @Output() chartAxisUpdated = new EventEmitter();
  
  public highcharts = Highcharts;
  public update: boolean = false;
  public chartRef;
  public chartOptions: Highcharts.Options = { ...HIGHCHART_OPTIONS_DEFAULT }
  private signalsOrder: { [k: string]: string | number};
  public customAxisActive: boolean = false;
  private unsubscriber$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private dataAnalysisService: DataAnalysisService,
    private themeService: ThemeService,
    private modalService: BsModalService,
    private customNumberFormat: CustomNumberFormatPipe,
    private dateFormatPipe: DateTimeFormatPreferencePipe,
    private timezoneService: TimezoneService,
    private dp: DateTimeFormatPreferencePipe,
    private excelExportService: ExcelExportService
  ) {
    this.chartOptions.xAxis = {
      type: 'linear',
      tickColor: 'var(--font)',
      lineColor: 'var(--font)',
      labels: { style: { color: 'var(--font)' } }
    }

    const customNumberFormatRef = this.customNumberFormat;
    const self = this;

    this.chartOptions.tooltip = {
      shared: true,
      valueDecimals: 2,
      formatter: function (a) {
        const element: any = this;
        const tsFormat = this.series.userOptions['tsFormat'] || ((ts,format) =>  self.dateFormatPipe.transform(ts, format));
        const formattedSignalY = customNumberFormatRef.transform(Number(element.y).toFixed(2));
        const formattedSignal = customNumberFormatRef.transform(Number(element.x).toFixed(2));
        return `
            <span class="font-size-very-small" style="color:${element.color}"> ● </span> 
            <span class="font-size-very-small">${element?.series?.userOptions?.assetDisplayName + ' | ' + element.series.name}</span><br/>
            <div class="font-size-very-small">${tsFormat(element.series.userOptions.timestampData[element.point.index], 'dmyt')}</div>
            <br/>${element.series.userOptions.unitY} ${element.series.userOptions.unitY === element.series.userOptions.unit ? '[Y-axis]' : ''}: <b>${formattedSignalY}</b>
            <br/>${element.series.userOptions.unit} ${element.series.userOptions.unitY === element.series.userOptions.unit ? '[X-axis]' : ''}: <b>${formattedSignal}</b>
          `
      },
    }

    this.chartOptions.yAxis =  {
      labels: {
        style: { color: 'var(--font)' },
        formatter: function () {
          return customNumberFormatRef.transform(this.value.toString());
        }
      }
    }

    this.chartOptions.legend = {
      ...this.chartOptions.legend,
      events: {
        itemClick: () => false
      }
    }

  }

  public ngOnInit(): void {
    this.themeService.getTheme().pipe(takeUntil(this.unsubscriber$)).subscribe(() => {
      this.updateTooltipTheme();
    });

    if(this.exportChart$){
      this.exportChart$.pipe(takeUntil(this.unsubscriber$)).subscribe(d => this.export(d));
    }

    this.dataAnalysisService.scatterPlotSignalAdd$.pipe(takeUntil(this.unsubscriber$), filter(v => v), delay(100)).subscribe(() => {
      const series = [];
      this.signalsData.data.forEach(s => {
        const index = this.chartRef?.series?.findIndex(serie => serie.userOptions.id === s.id);
        index === -1 && s.chartData.length !== 0 && series.push(this.formatSingleSerie(s))
      })
      this.redrawChart(series);
    });

    this.dataAnalysisService.scatterPlotSignalChange$.pipe(takeUntil(this.unsubscriber$), filter(v => v)).subscribe(signal => {
      const index = this.chartRef?.series?.findIndex(serie => serie?.userOptions?.id === signal?.id);
      index > -1 && this.chartRef?.series[index]?.update({ ...this.formatSingleSerie(signal), data: signal.chartData });
    });

    this.dataAnalysisService.scatterPlotPeriodChange$.pipe(takeUntil(this.unsubscriber$), filter(v => v)).subscribe(() => {
      this.deleteSeriesAndRedrawChart();
    });

    this.dataAnalysisService.scatterPlotSignalRemove$.pipe(takeUntil(this.unsubscriber$), filter(v => v)).subscribe(signals => {
      signals.ids.forEach(v => {
        const index = this.chartRef?.series?.findIndex(item => item.userOptions.id === v);
        const deletedSignalUnit = this.chartRef?.series?.find(item => item?.userOptions?.id === v)?.userOptions?.unit;
        index > -1 && this.chartRef?.series[index]?.remove();
        /* Check if there's not any signal with the same units than the one(s) we're deleting. If so, we remove the y-axis completely */
        if (this.chartRef?.series?.every(item => item.userOptions.unit !== deletedSignalUnit)) {
          index > -1 && this.chartRef?.yAxis[index]?.remove();
        }
      })
      this.chartOptions.yAxis = signals.yAxis;
      this.customAxisActive = this.signalsData.data.some(d => d.yAxisMin || d.yAxisMax);
      this.updateSeries();
    });
  }

  private updateTooltipTheme(): void {
    const tooltipTheme = {
      backgroundColor: this.themeService.getColor('--card'),
      style: {
        color: this.themeService.getColor('--primary')
      }
    };

    this.chartOptions.tooltip = {
      ...this.chartOptions.tooltip,
      ...tooltipTheme
    };
    this.chartRef?.update({ tooltip: tooltipTheme }, false);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.xAxisMin?.currentValue || changes.xAxisMax?.currentValue) {
      const xAxisOptions = this.chartOptions.xAxis;
      const axisToUpdate = Array.isArray(xAxisOptions) ? xAxisOptions[0] : xAxisOptions;

      if (axisToUpdate) {
        axisToUpdate.min = this.xAxisMin;
        axisToUpdate.max = this.xAxisMax;
      }
    }
    if (changes.signalsData?.currentValue && !this.chartRef) {
      this.customAxisActive = this.signalsData.data.some(d => d.yAxisMin || d.yAxisMax);
      this.redrawChart();
    }
  }

  private updateYAxisValues(data: {custom: boolean, cancel: boolean, yaxis: {min: number, max: number, title: string}[]}): void {
    if (data.custom) {
      this.customAxisActive = true;
      data.yaxis.forEach((yAxis, i) => {
        const signalsSharingUnits = this.signalsData.data.filter(d => d.unitY === yAxis.title);
        signalsSharingUnits.forEach(s => {s.yAxisMin = yAxis.min; s.yAxisMax = yAxis.max});
      });
    } else {
      this.customAxisActive = false;
      this.signalsData.data.forEach(s => {s.yAxisMin = null; s.yAxisMax = null})
    }
    this.deleteSeriesAndRedrawChart();
    this.chartAxisUpdated.emit();
  }

  public chartCallback: Highcharts.ChartCallbackFunction = chart => {
    if (!this.chartRef) {
      this.chartRef = chart;
    }
  };

  public openYAxisConfiguration(): void {
    const subject = new Subject<any>();
    const modal = this.modalService.show(SignalsChartYAxisPopupComponent, {
      backdrop: true,
      keyboard: true,
      ignoreBackdropClick: true,
      class: `modal-dialog-primary modal-md modal-dialog-centered ${this.themeService.getTheme().value}`
    });

    const yAxisData = this.signalsData.yAxis.map(y => {
      const index = this.chartRef.yAxis.findIndex(a => a.userOptions.title.text === y.title.text);
      return {
        min: this.chartRef.yAxis[index].min,
        max: this.chartRef.yAxis[index].max,
        title: this.chartRef.yAxis[index].userOptions.title.text,
        locked: this.chartRef.yAxis[index].userOptions.min !== null
      }
    })
    modal.content.subject = subject;
    modal.content.yAxisData = yAxisData;
    modal.content.theme$ = this.themeService.getTheme();

    subject.toPromise().then((response: {custom: boolean, cancel: boolean, yaxis: {min: number, max: number, title: string}[]}) => {
      if (response.cancel) {
        return;
      }
      this.updateYAxisValues(response);
    });
  }

  private deleteSeriesAndRedrawChart(): void {
    while (this.chartRef?.series?.length) {
      this.chartRef?.series[0]?.remove();
    }
    while (this.chartRef?.yAxis?.length) {
      this.chartRef?.yAxis[0]?.remove();
    }
    setTimeout(() => {
      this.redrawChart();
    }, 100);
  }

  private redrawChart(series = null): void {
    this.signalsOrder = Object.fromEntries(this.signalsData.data.map((k, i) => [k.id, i]));
    const chartSeries: Highcharts.SeriesOptionsType[] = (series ?? this.signalsData.data).map((item: any) => series ? item : this.formatSingleSerie(item));
    this.chartOptions.yAxis = this.signalsData.yAxis;
    setTimeout(() => {
      this.addSeries(chartSeries);
      this.updateSeries();
    }, 100);
  }

  public addSeries(series): void {
    this.updateXAxis();
    series.forEach(item => {
      const index = this.signalsData.yAxis.findIndex(el => el.title?.text === item.unitY);
      if (!this.chartRef.yAxis.find(el => el.userOptions?.title?.text === item.unitY)) {
        this.chartRef.addAxis(this.signalsData.yAxis[index]);
      }
      const newIndex = this.chartRef.yAxis.findIndex(el => el.userOptions?.title?.text === item.unitY)
      item.yAxis = newIndex;
      this.chartRef.addSeries(item);
    });
  }

  public removeSeries() {
    this.removeItems.ids.forEach(id => {
      const index = this.chartRef?.series.findIndex(item => item.userOptions.id === id);
      index > -1 && this.chartRef?.series[index].remove();
    });
    this.updateXAxis();
  }

  private updateSeries() {
    this.updateXAxis();
    this.updateSeriesList.forEach(item => {
      const index = this.chartRef.series.findIndex(serie => serie.userOptions.id === item.id);
      this.chartRef.series[index]?.update({ ...item, data: item.chartData });
    });
    this.updated.emit();
  }

  private updateXAxis() {
    if (this.chartRef?.xAxis && (this.xAxisMin !== this.chartRef?.xAxis[0]?.min || this.xAxisMax !== this.chartRef?.xAxis[0]?.max)) {
      this.chartRef?.xAxis[0]?.update({ min: this.xAxisMin, max: this.xAxisMax }, true);
    }
  }

  public export(index): void {
    const chartOptions = chartExportingOptions(this.signalsData, this.themeService);
    switch (index) {
      case 0: {
        const { dataJSON } = getSeriesTableData(this.signalsData.data, 'scatter-plot', this.dp, this.timezoneService.selectedOption);
        this.excelExportService.exportJSONasTable(dataJSON, 'Scatter Plot Chart', DataExportType.xlsx);
        break;
      };
      case 1: {
        const { dataJSON } = getSeriesTableData(this.signalsData.data, 'scatter-plot', this.dp, this.timezoneService.selectedOption);
        this.excelExportService.exportJSONasTable(dataJSON, 'Scatter Plot Chart', DataExportType.csv);
        break;
      };
      case 2: this.chartRef?.exportChartLocal({type: 'image/png'}, chartOptions); break;
      case 3: this.chartRef?.exportChartLocal({type: 'application/pdf'}, chartOptions); break;
    }
  }

  private formatSingleSerie(serie) {
    const device = `${serie.deviceType !== DEVICE_TYPES.PARK ? serie.device + ' | ' : ''}`;
    const deviceY = `${serie.deviceTypeY !== DEVICE_TYPES.PARK ? (serie.deviceY || serie.device) + ' | ' : ''}`;
    return {
      id: serie.id,
      assetDisplayName: serie.turbine?.replaceAll('_', ' ') || serie.parkDisplayName || '',
      tooltip: {
        xDateFormat: '%d %b %y %H %M'
      },
      type: serie.type,
      dashStyle: serie.dashStyle,
      marker: {
        enabled: true,
        symbol: serie.marker?.symbol,
        radius: 3
      },
      legendIndex: this.signalsOrder[serie.id],
      step: serie.step,
      name: `${deviceY}${serie.signalNameY}[${serie.unitY}] / ${device}${serie.signalName}[${serie.unit}]`,
      yAxis: serie.yAxis,
      color: serie.color,
      timestampData: serie?.chartDataX?.map(a => a[0]),
      data: serie.chartData,
      signal: serie.signal,
      signalY: serie.signalY,
      unit: serie.unit,
      unitY: serie.unitY,
      visible: serie.visible,
      yAxisMin: serie.yAxisMin,
      yAxisMax: serie.yAxisMax,
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber$.next(true);
    this.unsubscriber$.unsubscribe();
  }
}
